import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart,
  Sparkles,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleWishlist } from '../../store/slices/wishlistSlice';
import { formatPrice, getDisplayName, getDisplayImage } from '../../utils/helpers';
import { calculateDiscountedPrice } from '../../utils/priceUtils';
import { selectAllProducts } from '../../store/slices/productsSlice';
import toastManager from '../../utils/toastManager';
import { useTranslation } from 'react-i18next';
import { isFreeScriptProduct, isWishlistEligibleProduct, normalizeCategory } from '../../utils/productRules';
import {
  ThemeCardCornerDecoration,
  ThemeCardSeasonalBadge,
  ThemeCardAccentIcon,
  getThemeCardContainerClasses,
  getThemePriceClasses,
  getThemeWishlistClasses
} from '../common/ThemeCardDecorations';

// Stable constants to prevent unnecessary re-renders from Redux selectors
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const ProductCard = ({ product, index = 0, viewMode = 'grid', isCompact = false, enableAnimation = true, isMobile = false }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get wishlist state from Redux
  const wishlistItems = useSelector(state => state.wishlist?.items || EMPTY_ARRAY);
  const activeTheme = useSelector(state => state.ui?.activeTheme || 'default');
  const isWishlisted = wishlistItems.some(item => item.id === product.id);

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isWishlistEligibleProduct(product) && !isWishlisted) {
      toastManager.error(t('product.not_wishlistable', 'This product cannot be added to wishlist'));
      return;
    }

    const wasWishlisted = isWishlisted;
    dispatch(toggleWishlist(product));

    // Show appropriate toast message - only one toast per action
    if (wasWishlisted) {
      toastManager.success(t('product.removed_from_wishlist'));
    } else {
      toastManager.success(t('product.added_to_wishlist'));
    }
  };

  const handleCardClick = (e) => {
    // Prevent navigation if clicking on wishlist button
    if (e.target.closest('button')) {
      return;
    }
    navigate(`/product/${product.routeProductId || product.baseProductId || product.parentProductId || product.id}`);
  };

  // Robust price resolver: supports number/string on price and scans Price/pricingRaw map
  const resolvePrice = (p) => {
    if (!p) return null;
    const nums = [];

    // direct price
    if (typeof p.price === 'number' && isFinite(p.price) && p.price > 0) nums.push(p.price);
    if (typeof p.price === 'string') {
      const n = parseFloat(p.price.replace(/[^0-9.]/g, ''));
      if (!Number.isNaN(n) && n > 0) nums.push(n);
    }

    // Price/pricingRaw maps
    const map = p.Price || p.pricingRaw || p.priceOptions || null;
    if (map && typeof map === 'object') {
      for (const key of Object.keys(map)) {
        const v = map[key];
        if (typeof v === 'number' && isFinite(v) && v > 0) nums.push(v);
        else if (typeof v === 'string') {
          const n = parseFloat(v.replace(/[^0-9.]/g, ''));
          if (!Number.isNaN(n) && n > 0) nums.push(n);
        }
      }
    }

    if (nums.length === 0) return null;
    // Show the lowest available tier as the card price
    return Math.min(...nums);
  };

  // Get all products for bundle calculation using the memoized selector
  const allProducts = useSelector(selectAllProducts);
  const normalizedProductCategory = normalizeCategory(product?.category);
  const linkedCounterpart = React.useMemo(() => {
    if (!product?.linkedProductId || !Array.isArray(allProducts) || allProducts.length === 0) {
      return null;
    }

    const match = allProducts.find((item) => item.id === product.linkedProductId);
    if (!match) return null;

    const counterpartCategory = normalizeCategory(match.category);
    if (normalizedProductCategory === 'free') {
      return ['premium', 'exclusive'].includes(counterpartCategory) ? match : null;
    }
    if (normalizedProductCategory === 'premium' || normalizedProductCategory === 'exclusive') {
      return counterpartCategory === 'free' ? match : null;
    }
    return null;
  }, [allProducts, normalizedProductCategory, product?.linkedProductId]);
  const counterpartLabel = linkedCounterpart
    ? (normalizedProductCategory === 'free' ? 'Paid version available' : 'Free version available')
    : '';

  // Calculate bundle original price dynamically
  const bundleOriginalPrice = React.useMemo(() => {
    if (product?.category !== 'bundle' || !Array.isArray(product?.Products)) return null;

    const productMap = new Map();
    allProducts.forEach(p => {
      if (p && p.id) productMap.set(String(p.id), p);
    });

    let total = 0;
    product.Products.forEach(id => {
      const item = productMap.get(String(id));
      if (item) {
        const itemPrice = resolvePrice(item);
        if (itemPrice) total += itemPrice;
      }
    });

    return total > 0 ? total : null;
  }, [product, allProducts]);

  // Get store discounts
  const storeDiscounts = useSelector(state => state.products?.storeDiscounts || EMPTY_OBJECT);

  // Determine if product is purchasable
  const isActive = !product.status || product.status === 'Active' || product.status === 'active';
  const isFreeScript = isFreeScriptProduct(product);
  const wishlistEnabled = isWishlistEligibleProduct(product);

  const displayPrice = resolvePrice(product);
  const displayName = getDisplayName(product);
  const displayImage = getDisplayImage(product);
  const originalPriceToDisplay = bundleOriginalPrice || product.originalPrice;

  const getValidityForPrice = (p, targetPrice) => {
    const map = p.pricingRaw || p.Price || {};
    for (const [key, val] of Object.entries(map)) {
      if (extractNumericPrice(val) === targetPrice) return key;
    }
    return 'monthly';
  };

  const extractNumericPrice = (p) => {
    if (typeof p === 'number') return p;
    if (typeof p === 'string') {
      const m = p.match(/[\d.]+/);
      return m ? parseFloat(m[0]) : 0;
    }
    return 0;
  };

  const detectedValidity = getValidityForPrice(product, displayPrice);

  // Calculate potential discount
  const { finalPrice, isDiscounted, discountPercentage } = calculateDiscountedPrice(product, displayPrice, detectedValidity, storeDiscounts);

  // If we have a store discount, override the display
  const effectivePrice = isDiscounted ? finalPrice : displayPrice;
  const effectiveOriginalPrice = isDiscounted ? displayPrice : originalPriceToDisplay;
  const showDiscountBadge = isDiscounted || product.discount;
  const displayDiscountPercent = isDiscounted ? discountPercentage : product.discount;

  // List view layout (compact horizontal)
  if (viewMode === 'list') {
    return (
      <motion.div
        initial={enableAnimation ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={enableAnimation ? { duration: 0.5, delay: index * 0.05 } : { duration: 0 }}
        className={`group cursor-pointer overflow-hidden rounded-3xl border shadow-sm transition-all duration-300 hover:-translate-y-0.5 ${getThemeCardContainerClasses(activeTheme, 'list')}`}
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-3 p-2.5 sm:gap-4 sm:p-3">
          {/* Compact Product Image */}
          <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800">
            <ThemeCardCornerDecoration activeTheme={activeTheme} className="w-6 h-6" />
            <div className={`absolute ${isActive && showDiscountBadge ? 'bottom-1 start-1' : 'top-1 start-1'} z-10`}>
              <ThemeCardSeasonalBadge activeTheme={activeTheme} isCompact={true} />
            </div>
            <div className="absolute bottom-1 end-1 z-10 opacity-70 group-hover:opacity-100 transition-opacity">
              <ThemeCardAccentIcon activeTheme={activeTheme} />
            </div>
            <motion.img
              src={displayImage}
              alt={displayName}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${!isActive ? 'opacity-60 grayscale' : ''}`}
              loading="lazy"
            />
            {!isActive && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <span className="bg-gray-800/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{t('product.unavailable', 'Unavailable')}</span>
              </div>
            )}
            {isActive && showDiscountBadge && (
              <div className="absolute start-1.5 top-1.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black text-white shadow-sm">
                {isDiscounted ? `${displayDiscountPercent}% ${t('product.off')}` : `-${displayDiscountPercent}%`}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <h3 className={`mb-1 line-clamp-1 text-sm font-bold leading-tight sm:text-base notranslate transition-colors ${activeTheme !== 'default' ? 'text-white group-hover:text-primary-300' : 'text-gray-950 dark:text-white'}`} translate="no">
              {displayName}
            </h3>
            {counterpartLabel && (
              <div className="mb-1 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {counterpartLabel}
              </div>
            )}
            <div className="flex flex-col items-start gap-0.5">
              {effectiveOriginalPrice && (
                <span className="text-xs text-gray-500 line-through notranslate" translate="no">
                  {formatPrice(effectiveOriginalPrice)}
                </span>
              )}
              <div className="flex items-baseline gap-1">
                {isFreeScript ? (
                  <span className="text-lg font-black uppercase text-emerald-600 dark:text-emerald-400">FREE</span>
                ) : (
                  <span className={`text-lg font-black notranslate ${getThemePriceClasses(activeTheme)}`} translate="no">
                    {effectivePrice != null ? formatPrice(effectivePrice) : formatPrice(0)}
                  </span>
                )}
                {product.billing && (
                  <span className="text-sm text-primary-600 dark:text-primary-400 font-semibold">/{product.billing}</span>
                )}
              </div>
            </div>
          </div>

          {/* Wishlist Button */}
          {wishlistEnabled && (
            <button
              onClick={handleToggleWishlist}
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl border transition-colors duration-200 ${getThemeWishlistClasses(activeTheme, isWishlisted)}`}
            >
              <Heart
                className={`w-5 h-5 ${isWishlisted
                  ? 'text-red-500 fill-current'
                  : 'text-gray-600 dark:text-gray-400'
                  }`}
              />
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  // Mobile-optimized layout (automatic)
  if (isMobile || isCompact) {
    return (
      <motion.div
        initial={enableAnimation ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={enableAnimation ? { duration: 0.5, delay: index * 0.1 } : { duration: 0 }}
        className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border p-1.5 transition-all duration-300 hover:-translate-y-0.5 ${getThemeCardContainerClasses(activeTheme, 'grid')}`}
        onClick={handleCardClick}
      >
        {/* Mobile Product Image */}
        <div className="relative aspect-square shrink-0 overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800">
          <ThemeCardCornerDecoration activeTheme={activeTheme} className="w-7 h-7" />
          <div className={`absolute ${isActive && showDiscountBadge ? 'bottom-1.5 start-1.5' : 'top-1.5 start-1.5'} z-10`}>
            <ThemeCardSeasonalBadge activeTheme={activeTheme} isCompact={isCompact} isMobile={isMobile} />
          </div>
          <div className="absolute bottom-1.5 end-1.5 z-10 opacity-70 group-hover:opacity-100 transition-opacity">
            <ThemeCardAccentIcon activeTheme={activeTheme} />
          </div>
          <motion.img
            src={displayImage}
            alt={displayName}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${!isActive ? 'opacity-60 grayscale' : ''}`}
            loading="lazy"
          />

          {/* Unavailable overlay */}
          {!isActive && (
            <div className="absolute inset-0 flex items-end justify-start p-1.5 bg-black/20">
              <span className="bg-gray-900/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{t('product.unavailable', 'Unavailable')}</span>
            </div>
          )}

          {/* Mobile Wishlist Button */}
          {isActive && wishlistEnabled && (
            <button
              onClick={handleToggleWishlist}
              className={`absolute end-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full shadow-lg transition-colors duration-200 ${getThemeWishlistClasses(activeTheme, isWishlisted)}`}
            >
              <Heart
                className={`w-3.5 h-3.5 ${isWishlisted
                  ? 'text-red-500 fill-current'
                  : 'text-gray-600 dark:text-gray-400'
                  }`}
              />
            </button>
          )}

          {/* Mobile Sale Badge */}
          {isActive && showDiscountBadge && (
            <div className="absolute start-2 top-2 rounded-full bg-red-500 px-2 py-1 text-[10px] font-black text-white shadow-sm">
              {isDiscounted ? `${displayDiscountPercent}% ${t('product.off')}` : `-${displayDiscountPercent}%`}
            </div>
          )}
        </div>

        {/* Mobile Product Info */}
        <div className="flex flex-1 flex-col p-2.5">
          {/* Title + Rating */}
          <div className="flex flex-col gap-1.5">
            <h3 className={`line-clamp-2 text-sm font-bold leading-tight notranslate transition-colors ${activeTheme !== 'default' ? 'text-white group-hover:text-primary-300' : 'text-gray-950 dark:text-white'}`} translate="no">
              {displayName}
            </h3>
            {counterpartLabel && (
              <div className="inline-flex w-fit rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {counterpartLabel}
              </div>
            )}
          </div>

          {/* Price - positioned closer to title */}
          <div className="mt-1 text-start">
            <div className="flex flex-col items-start gap-0.5">
              {effectiveOriginalPrice && (
                <span className="text-xs text-gray-500 line-through notranslate" translate="no">
                  {formatPrice(effectiveOriginalPrice)}
                </span>
              )}
              <div className="flex items-baseline gap-1.5">
                {isFreeScript ? (
                <span className="font-bold text-emerald-600 dark:text-emerald-400 text-base uppercase">FREE</span>
              ) : (
                  <span className={`text-base font-black notranslate ${getThemePriceClasses(activeTheme)}`} translate="no">
                    {effectivePrice != null ? formatPrice(effectivePrice) : formatPrice(0)}
                  </span>
                )}
                {product.billing && (
                  <span className="text-xs text-primary-600 dark:text-primary-400 font-semibold">/{product.billing}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop layout
  return (
    <motion.div
      initial={enableAnimation ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={enableAnimation ? { duration: 0.5, delay: index * 0.1 } : { duration: 0 }}
      className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border p-1.5 transition-all duration-300 hover:-translate-y-1 ${getThemeCardContainerClasses(activeTheme, 'grid')}`}
      onClick={handleCardClick}
    >
      {/* Desktop Product Image */}
      <div className="relative aspect-square shrink-0 overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800">
        <ThemeCardCornerDecoration activeTheme={activeTheme} className="w-9 h-9" />
        <div className={`absolute ${isActive && showDiscountBadge ? 'bottom-2.5 start-2.5' : 'top-2 start-2'} z-10`}>
          <ThemeCardSeasonalBadge activeTheme={activeTheme} isCompact={false} isMobile={false} />
        </div>
        <div className="absolute bottom-2.5 end-2.5 z-10 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
          <ThemeCardAccentIcon activeTheme={activeTheme} />
        </div>
        <motion.img
          src={displayImage}
          alt={displayName}
          className={`w-full h-full object-cover ${!isActive ? 'opacity-60 grayscale' : ''}`}
          loading="lazy"
        />

        {/* Unavailable overlay */}
        {!isActive && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <span className="bg-gray-900/80 text-white text-xs font-bold px-3 py-1.5 rounded-lg tracking-wide">{t('product.unavailable', 'Unavailable')}</span>
          </div>
        )}

        {/* Desktop Overlay Actions (wishlist only) */}
        {isActive && wishlistEnabled && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <button
              onClick={handleToggleWishlist}
              className={`flex h-11 w-11 items-center justify-center rounded-full shadow-lg transition-colors duration-200 ${getThemeWishlistClasses(activeTheme, isWishlisted)}`}
            >
              <Heart
                className={`w-5 h-5 ${isWishlisted
                  ? 'text-red-500 fill-current'
                  : 'text-gray-600'
                  }`}
              />
            </button>
          </div>
        )}

        {/* Desktop Sale Badge */}
        {isActive && showDiscountBadge && (
          <div className="absolute start-2.5 top-2.5 rounded-full bg-red-500 px-2.5 py-1 text-xs font-black text-white shadow-sm">
            {isDiscounted ? `${displayDiscountPercent}% ${t('product.off')}` : `-${displayDiscountPercent}%`}
          </div>
        )}

        {isFreeScript && (
          <div className="absolute bottom-2.5 start-2.5 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-1 text-[10px] font-black uppercase text-white shadow-sm">
            <Sparkles className="h-3 w-3" />
            Free
          </div>
        )}
      </div>

      {/* Desktop Product Info */}
      <div className="flex flex-1 flex-col p-3.5">
        {/* Title + Rating */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <h3 className={`line-clamp-2 flex-1 text-base font-bold leading-snug notranslate transition-colors ${activeTheme !== 'default' ? 'text-white group-hover:text-primary-300' : 'text-gray-950 dark:text-white'}`} translate="no">
              {displayName}
            </h3>
          </div>
          {counterpartLabel && (
            <div className="inline-flex w-fit rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
              {counterpartLabel}
            </div>
          )}
        </div>

        {/* Price - positioned closer to title */}
        <div className="mt-1 text-start">
          <div className="flex flex-col items-start gap-0.5">
            {effectiveOriginalPrice && (
              <span className="text-sm text-gray-500 line-through notranslate" translate="no">
                {formatPrice(effectiveOriginalPrice)}
              </span>
            )}
            <div className="flex items-baseline gap-2">
              {isFreeScript ? (
                <span className="text-lg font-black uppercase text-emerald-600 dark:text-emerald-400">FREE</span>
              ) : (
                <span className={`text-lg font-black notranslate ${getThemePriceClasses(activeTheme)}`} translate="no">
                  {effectivePrice != null ? formatPrice(effectivePrice) : formatPrice(0)}
                </span>
              )}
              {product.billing && (
                <span className="text-sm text-primary-600 dark:text-primary-400 font-semibold">/{product.billing}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
